import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import HospitalLogo from '@medical/shared/components/HospitalLogo';

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #0B63C5 0%, #0F1F4D 100%);
  color: white;
  padding: 1.25rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.div`
  h1 {
    margin: 0;
    font-size: 1.5rem;
  }

  p {
    margin: 0.25rem 0 0;
    opacity: 0.9;
    font-size: 0.875rem;
  }
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Content = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 960px) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`;

const Card = styled.section`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const CardTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  color: #1f2937;
`;

const CardSubtitle = styled.p`
  margin: 0 0 1.25rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const RoleBanner = styled.div`
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f0f4ff;
  border-radius: 8px;
  border: 1px solid #0B63C5;

  strong {
    color: #0B63C5;
  }
`;

const FormSection = styled.div`
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #0B63C5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const FieldError = styled.span`
  color: #dc2626;
  font-size: 0.8rem;
`;

const ReadOnlyValue = styled.div`
  padding: 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.95rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #0B63C5 0%, #0F1F4D 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.85rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessCard = styled.div`
  margin-top: 1.25rem;
  padding: 1rem;
  border-radius: 12px;
  background: #F8FAFC;
  border: 1px solid #fcd34d;
`;

const SuccessTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: #0F1F4D;
  font-size: 1rem;
`;

const CredentialRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #fde68a;

  &:last-child {
    border-bottom: none;
  }

  span:first-child {
    color: #78350f;
    font-size: 0.875rem;
  }

  strong {
    color: #0F1F4D;
    font-family: monospace;
    font-size: 1rem;
  }
`;

const CopyButton = styled.button`
  background: #E2E8F0;
  color: #0F1F4D;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
`;

const PatientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 520px;
  overflow-y: auto;
`;

const PatientItem = styled.div`
  padding: 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
`;

const PatientName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const PatientMeta = styled.div`
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const EmptyState = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const initialForm = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  phone: '',
  assignedDoctor: ''
};

const todayIso = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const validatePatientForm = (form) => {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  const birth = new Date(form.dateOfBirth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!form.dateOfBirth || Number.isNaN(birth.getTime())) errors.dateOfBirth = 'Date of birth is required';
  else if (birth >= today) errors.dateOfBirth = 'Date of birth must be before today';
  if (!form.gender) errors.gender = 'Gender is required';
  if (!form.phone.trim()) errors.phone = 'Phone number is required';
  else if (!/^[6-9]\d{9}$/.test(form.phone.trim())) errors.phone = 'Enter a 10-digit mobile number';
  if (!form.assignedDoctor) errors.assignedDoctor = 'Please select a doctor';
  return errors;
};

const FrontDeskDashboard = () => {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('frontDeskUser');
    if (stored) {
      setUser(JSON.parse(stored));
    }
    fetchDoctors();
    fetchPatients();
  }, []);

  const fetchDoctors = async () => {
    try {
      setLoadingDoctors(true);
      const response = await axios.get('/api/auth/doctors');
      setDoctors(response.data.doctors || []);
    } catch (error) {
      toast.error('Failed to load doctors list');
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      const response = await axios.get('/api/auth/front-desk/patients');
      setPatients(response.data.patients || []);
    } catch (error) {
      toast.error('Failed to load registered patients');
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    const value = name === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validatePatientForm(formData);
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSubmitting(true);
    setRegistrationResult(null);

    const payload = {
      ...formData,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phone: formData.phone.trim()
    };

    try {
      const response = await axios.post('/api/auth/front-desk/patients', payload);
      setRegistrationResult({
        patientId: response.data.patientId,
        temporaryPassword: response.data.temporaryPassword,
        phone: response.data.patient?.phone || payload.phone,
        patientName: `${formData.firstName} ${formData.lastName || ''}`.trim()
      });
      toast.success('Patient registered successfully');
      setFormData(initialForm);
      fetchPatients();
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const message = validationErrors?.length
        ? validationErrors.map((err) => err.msg).join('. ')
        : (error.response?.data?.message || 'Registration failed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleLogout = async () => {
    try {
      const sessionId = localStorage.getItem('frontDeskSessionId');
      await axios.post('/api/auth/logout', sessionId ? { sessionId } : {});
    } catch (error) {
      // Continue with local logout even if API fails
    }

    localStorage.removeItem('frontDeskToken');
    localStorage.removeItem('frontDeskRefreshToken');
    localStorage.removeItem('frontDeskUser');
    localStorage.removeItem('frontDeskSessionId');
    delete axios.defaults.headers.common['Authorization'];
    window.dispatchEvent(new Event('frontDeskLogout'));
    window.location.href = '/front-desk';
  };

  return (
    <Page>
      <Header>
        <HeaderTitle style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ width: 64, height: 64, borderRadius: 10, flexShrink: 0 }} />
          <HospitalLogo logoUrl={user?.hospitalLogoUrl} name={user?.hospitalName} size={64} style={{ flexShrink: 0 }} />
          <div>
          <h1>Front Desk</h1>
          <p>
            {user ? `${user.fullName || user.firstName} (${user.frontDeskId})` : 'Patient registration portal'}
          </p>
          </div>
        </HeaderTitle>
        <LogoutButton type="button" onClick={handleLogout}>
          Logout
        </LogoutButton>
      </Header>

      <Content>
        <Card>
          <CardTitle>Register New Patient</CardTitle>
          <CardSubtitle>
            Create a patient account and share the Patient ID and temporary password with them for login.
          </CardSubtitle>

          <Form onSubmit={handleSubmit} noValidate>
            <RoleBanner>
              <strong>Registering as: Patient</strong>
            </RoleBanner>

            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>

              <FormGroup>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  aria-invalid={Boolean(fieldErrors.firstName)}
                />
                {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Last Name (Optional)</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name (optional)"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                  <Input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    max={todayIso()}
                    aria-invalid={Boolean(fieldErrors.dateOfBirth)}
                  />
                  {fieldErrors.dateOfBirth && <FieldError>{fieldErrors.dateOfBirth}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="gender">Gender *</Label>
                  <Select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(fieldErrors.gender)}
                  >
                    <option value="">-- Select Gender --</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </Select>
                  {fieldErrors.gender && <FieldError>{fieldErrors.gender}</FieldError>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit contact number"
                  aria-invalid={Boolean(fieldErrors.phone)}
                />
                {fieldErrors.phone && <FieldError>{fieldErrors.phone}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label as="span">Hospital</Label>
                <ReadOnlyValue>
                  {user?.hospitalName ? `${user.hospitalName} · ` : ''}{user?.hospitalId || 'Your hospital'}
                </ReadOnlyValue>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="assignedDoctor">Select Doctor *</Label>
                <Select
                  id="assignedDoctor"
                  name="assignedDoctor"
                  value={formData.assignedDoctor}
                  onChange={handleInputChange}
                  disabled={loadingDoctors}
                  aria-invalid={Boolean(fieldErrors.assignedDoctor)}
                >
                  <option value="">
                    {loadingDoctors ? 'Loading doctors...' : '-- Select a Doctor --'}
                  </option>
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.fullName} ({doctor.doctorId})
                    </option>
                  ))}
                </Select>
                {fieldErrors.assignedDoctor && <FieldError>{fieldErrors.assignedDoctor}</FieldError>}
                {!loadingDoctors && doctors.length === 0 && (
                  <FieldError>No doctors available. Ask your hospital admin to add a doctor.</FieldError>
                )}
              </FormGroup>
            </FormSection>

            <SubmitButton type="submit" disabled={submitting || loadingDoctors}>
              {submitting ? 'Registering...' : 'Register Patient'}
            </SubmitButton>
          </Form>

          {registrationResult && (
            <SuccessCard role="status">
              <SuccessTitle>Patient registered. Share these details with {registrationResult.patientName}</SuccessTitle>
              <CredentialRow>
                <span>Registered Phone</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong>{registrationResult.phone}</strong>
                  <CopyButton
                    type="button"
                    onClick={() => copyToClipboard('Phone number', registrationResult.phone)}
                  >
                    Copy
                  </CopyButton>
                </div>
              </CredentialRow>
              <CredentialRow>
                <span>Patient ID</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong>{registrationResult.patientId}</strong>
                  <CopyButton
                    type="button"
                    onClick={() => copyToClipboard('Patient ID', registrationResult.patientId)}
                  >
                    Copy
                  </CopyButton>
                </div>
              </CredentialRow>
              <CredentialRow>
                <span>Default Password</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <strong>{registrationResult.temporaryPassword}</strong>
                  <CopyButton
                    type="button"
                    onClick={() => copyToClipboard('Password', registrationResult.temporaryPassword)}
                  >
                    Copy
                  </CopyButton>
                </div>
              </CredentialRow>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.85rem' }}>
                <CopyButton
                  type="button"
                  style={{ padding: '0.55rem 0.9rem', fontSize: '0.85rem' }}
                  onClick={() => copyToClipboard(
                    'Login details',
                    `Anuvedana login for ${registrationResult.patientName}\nRegistered phone: ${registrationResult.phone}\nPatient ID: ${registrationResult.patientId}\nPassword: ${registrationResult.temporaryPassword}\nPlease set a new password after your first sign-in.`
                  )}
                >
                  Copy all details
                </CopyButton>
                <CopyButton
                  type="button"
                  style={{ padding: '0.55rem 0.9rem', fontSize: '0.85rem' }}
                  onClick={() => setRegistrationResult(null)}
                >
                  Done
                </CopyButton>
              </div>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                The patient signs in with the Patient ID and this password, then sets a new password.
              </p>
            </SuccessCard>
          )}
        </Card>

        <Card>
          <CardTitle>Recently Registered</CardTitle>
          <CardSubtitle>Patients you have registered in this portal.</CardSubtitle>

          {loadingPatients ? (
            <EmptyState>Loading patients...</EmptyState>
          ) : patients.length === 0 ? (
            <EmptyState>No patients registered yet.</EmptyState>
          ) : (
            <PatientList>
              {patients.map(patient => (
                <PatientItem key={patient._id}>
                  <PatientName>{patient.fullName}</PatientName>
                  <PatientMeta>
                    ID: {patient.patientId}
                    {patient.hospitalId ? ` · Hospital ID: ${patient.hospitalId}` : ''}
                  </PatientMeta>
                  <PatientMeta>
                    Doctor: {patient.assignedDoctor
                      ? `Dr. ${patient.assignedDoctor.firstName} ${patient.assignedDoctor.lastName || ''}`.trim()
                      : 'Not assigned'}
                  </PatientMeta>
                  <PatientMeta>
                    Registered: {new Date(patient.registeredDate || patient.createdAt).toLocaleString()}
                  </PatientMeta>
                </PatientItem>
              ))}
            </PatientList>
          )}
        </Card>
      </Content>
    </Page>
  );
};

export default FrontDeskDashboard;
